import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};

// Theme CSS variables mapping
const applyThemeCSS = (config) => {
  const root = document.documentElement;
  
  // Convert hex to HSL for CSS variables
  const hexToHSL = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return "0 0% 0%";
    
    let r = parseInt(result[1], 16) / 255;
    let g = parseInt(result[2], 16) / 255;
    let b = parseInt(result[3], 16) / 255;
    
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }
    
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };
  
  // Apply theme colors
  root.style.setProperty("--primary", hexToHSL(config.primary));
  root.style.setProperty("--primary-foreground", hexToHSL(config.primary_foreground));
  root.style.setProperty("--secondary", hexToHSL(config.secondary));
  root.style.setProperty("--secondary-foreground", hexToHSL(config.primary_foreground));
  root.style.setProperty("--accent", hexToHSL(config.accent));
  root.style.setProperty("--accent-foreground", hexToHSL(config.primary_foreground));
  root.style.setProperty("--background", hexToHSL(config.background));
  root.style.setProperty("--foreground", hexToHSL(config.foreground));
  root.style.setProperty("--card", hexToHSL(config.card));
  root.style.setProperty("--card-foreground", hexToHSL(config.foreground));
  root.style.setProperty("--muted", hexToHSL(config.muted));
  root.style.setProperty("--destructive", hexToHSL(config.destructive));
  
  // Update theme-color meta tag
  const metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) {
    metaTheme.setAttribute("content", config.primary);
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableThemes, setAvailableThemes] = useState([]);

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      const res = await axios.get(`${API}/theme`);
      setTheme(res.data.active_theme);
      setAvailableThemes(res.data.available_themes);
      applyThemeCSS(res.data.config);
    } catch (err) {
      console.error("Failed to fetch theme:", err);
      // Apply default Rapido theme on error
      applyThemeCSS({
        primary: "#FFCE00",
        primary_foreground: "#000000",
        secondary: "#1A1A1A",
        accent: "#FFE566",
        background: "#FFFFFF",
        foreground: "#1A1A1A",
        card: "#FFFFFF",
        muted: "#F5F5F5",
        destructive: "#DC2626"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateTheme = async (newTheme) => {
    try {
      const res = await axios.put(`${API}/admin/theme`, { theme: newTheme });
      setTheme(res.data.active_theme);
      applyThemeCSS(res.data.config);
      return true;
    } catch (err) {
      console.error("Failed to update theme:", err);
      return false;
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, loading, availableThemes, updateTheme, refreshTheme: fetchTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
